import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../../../api/apiClient';
import { useApiQuery } from '../../../../../api/hooks';
import { usePreloaderStore } from '../../../../../stores';
import { Endpoints, QueryKey } from '../../../../../views/democracy/constants';
import { extractErrorMessage, buildSingleFileMultipart } from '../../../../../views/democracy/utils';
import useDemocracyMode from '../../../../../views/democracy/useDemocracyMode';
import { DownloadIcon, FolderIcon } from '../../../model/icons';
import './bundle.css';

// Под-таб «Bundle»: состав игры, экспорт архива (online) и импорт архива.
export default function BundlePanel({ democracy }) {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const { isOnline } = useDemocracyMode();
    const { show: showPreloader, hide: hidePreloader } = usePreloaderStore();
    const [includeMedia, setIncludeMedia] = useState(true);
    const importInputRef = useRef(null);

    const { data: full } = useApiQuery(
        QueryKey.democracyFull(democracy.id),
        Endpoints.democracies.get(democracy.id),
        { refetchOnMount: 'always' }
    );

    const handleDownload = async () => {
        showPreloader();
        try {
            // Bundle с медиа — 100+ MB, на медленной сети качается дольше общего таймаута 30с.
            const response = await apiClient.get(Endpoints.democracies.bundleDownload(democracy.id), {
                responseType: 'blob',
                params: { includeMedia },
                timeout: 0
            });
            const url = URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
            const slug = (democracy.title || `democracy-${democracy.id}`)
                .toLowerCase().replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '');
            const link = document.createElement('a');
            link.href = url;
            link.download = `${slug || 'democracy'}-v${democracy.version || 1}.zip`;
            link.click();
            URL.revokeObjectURL(url);
            enqueueSnackbar('Bundle скачивается', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось скачать bundle'), { variant: 'error' });
        } finally {
            hidePreloader();
        }
    };

    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        showPreloader();
        try {
            const { data } = await apiClient.post(Endpoints.bundle.upload, buildSingleFileMultipart(file));
            const r = data || {};
            const parts = `раундов ${r.rounds ?? 0}, вопросов ${r.questions ?? 0}, санкций ${r.sanctions ?? 0}, команд ${r.teams ?? 0}, медиа ${r.mediaFiles ?? 0}`;
            enqueueSnackbar(`${r.message || 'Bundle импортирован'} (${parts})`, { variant: 'success' });
            queryClient.invalidateQueries({ queryKey: ['democracy'] });
        } catch (error) {
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось импортировать bundle'), { variant: 'error' });
        } finally {
            hidePreloader();
        }
    };

    const rounds = full?.rounds || [];
    const stats = [
        { k: 'Раунды', v: rounds.length },
        { k: 'Вопросы', v: rounds.reduce((sum, r) => sum + (r.questions || []).length, 0) },
        { k: 'Санкции', v: (full?.sanctions || []).length },
        { k: 'Команды', v: (full?.teams || []).length }
    ];

    return (
        <div className="bn-card">
            <h2>{democracy.title || 'Демократия'}</h2>
            {democracy.description && <div className="bn-desc">{democracy.description}</div>}

            {full && (
                <div>
                    <label className="ctl-label">Состав игры</label>
                    <div className="bn-stats">
                        {stats.map(stat => (
                            <div className="bn-stat" key={stat.k}>
                                <div className="k">{stat.k}</div>
                                <div className="v">{stat.v}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {isOnline && (
                <>
                    <label className="bn-check">
                        <input type="checkbox" checked={includeMedia} onChange={(e) => setIncludeMedia(e.target.checked)} />
                        Включать медиа-файлы (вопросов, раундов, санкций, аудио команд)
                    </label>
                    <button type="button" className="btn btn-primary" onClick={handleDownload}>
                        <span className="ic"><DownloadIcon /></span>Скачать bundle.zip
                    </button>
                </>
            )}

            <input
                ref={importInputRef}
                type="file"
                accept=".zip,application/zip"
                style={{ display: 'none' }}
                onChange={handleImportFile}
            />
            <button type="button" className="btn btn-ghost" onClick={() => importInputRef.current?.click()}>
                <span className="ic"><FolderIcon /></span>Импортировать bundle.zip
            </button>

            <div className="bn-note">
                Импорт создаёт новую игру из архива.{!isOnline && ' Экспорт доступен только в online-режиме.'}
            </div>
        </div>
    );
}

BundlePanel.propTypes = {
    democracy: PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string,
        description: PropTypes.string,
        version: PropTypes.number
    }).isRequired
};
